<template>
  <div class="home">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <clientNavSelector></clientNavSelector>
      </el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: `/${currentClientId}/requests` }">Requests</el-breadcrumb-item>
      <el-breadcrumb-item>New Coding Schedule Request
      </el-breadcrumb-item>
    </el-breadcrumb>
    <hia-loading v-if="loading"></hia-loading>
    <div v-if="!loading">
      <el-row :gutter="40" style="margin-top: 40px;">
        <el-col :span="5">
          <div>
            <el-steps id="requestSteps" direction="vertical" :active="activeStep" finish-status="success">
              <el-step title="Request Basics">
                <div slot="description">
                  <p>Please select Coding Type and Requested Month.</p>
                  <p> For scheduling requests within 14 days, please
                    contact HIA directly.</p>
                </div>
              </el-step>
              <el-step :title="`Add Coders ${newScheduleRequest.month && 'For ' + newScheduleRequest.month}`">
                <div slot="description">
                  <p>Use the top section to add coders to your schedule request.</p>
                  <p v-if="newScheduleRequest.serviceLine=== 'Professional Fee'">For each coder, please select
                    Specialties and Coding Details.</p>
                  <p v-if="newScheduleRequest.serviceLine=== 'Acute'">For each coder, please select
                    Patient Types.</p>
                  <p>Once you have added coders to the request, you can edit their information in the bottom section.
                  </p>
                </div>
              </el-step>
              <el-step title="Confirm">
                <div slot="description">
                  <p>Confirm your request and choose Submit Request to send to HIA.</p>
                </div>
              </el-step>
            </el-steps>
          </div>
        </el-col>
        <el-col :span="19">

          <transition name="fade" mode="out-in">
            <!-- Step 0 -->
            <div key="step0" v-if="activeStep === 0">
              <el-form label-position="top" ref="newScheduleRequest" :rules="newScheduleRequest.rules" :model="newScheduleRequest" @submit.prevent.native>
                <el-form-item label="Coding Type">
                  <el-radio-group v-model="newScheduleRequest.serviceLine">
                    <el-radio label="Acute">Acute</el-radio>
                    <el-radio label="Professional Fee">Professional Fee</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item prop="month" label="Requested Month">
                  <el-select v-model="newScheduleRequest.month">
                    <el-option v-for="month in schedulePeriods" :key="month.key" :label="month.key" :value="month.key">
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-form>

              <el-button type="primary" id="next" style="margin-top: 20px;" @click="completeStepZero">Next <i style="padding-left: 5px;" class="el-icon-arrow-down"></i>
              </el-button>
              <hia-loading style="margin-top: 40px;" v-if="submitting"></hia-loading>

            </div>

            <!-- Step 1 -->
            <div key="step1" v-if="activeStep === 1">
              <CodingRequestAcuteCoderDetails v-if="newScheduleRequest.serviceLine === 'Acute'" :coders="newScheduleRequest.coders" :startDate="getCalendarPickerDate()" @add="addCoders" @delete="deleteCoders" @clearCoders="clearCoders">
              </CodingRequestAcuteCoderDetails>

              <CodingRequestProCoderDetails v-if="newScheduleRequest.serviceLine === 'Professional Fee'" :coders="newScheduleRequest.coders" :startDate="getCalendarPickerDate()" @add="addCoders" @delete="deleteCoders" @clearCoders="clearCoders">
              </CodingRequestProCoderDetails>

              <el-button plain style="margin-top: 30px; margin-bottom: 20px" @click="modifyPrevious">
                Modify Previous <i style="padding-left: 5px;" class="el-icon-arrow-up"></i>
              </el-button>

              <el-button title="Add coders before moving to the next step" type="primary" :disabled="newScheduleRequest.coders.length === 0" style="margin-top: 30px;" @click="completeStepOne">
                Next <i style="padding-left: 5px;" class="el-icon-arrow-down"></i>
              </el-button>
            </div>

            <!-- Step 2 -->
            <div key="step2" v-if="activeStep === 2">
              <h4 class="heading">Please Confirm Request for {{ newScheduleRequest.month }}
                ({{ getNumberOfWorkingDays() }}
                Work Days Available)
              </h4>

              <MasterCalendarPicker style="margin: 0px 10px 0px 10px;" :coders="newScheduleRequest.coders" :pageDate="getCalendarPickerDate()">
              </MasterCalendarPicker>

              <el-table id="confirmTable" ref="confirmTable" class="top-margin" :data="newScheduleRequest.coders">
                <el-table-column prop="coder" label="Coder" width="120">
                </el-table-column>
                <el-table-column v-if="newScheduleRequest.serviceLine === 'Acute'" prop="patientTypes" label="Patient Types">
                  <template slot-scope="scope">
                    {{scope.row.patientTypes.join(', ')}}
                  </template>
                </el-table-column>
                <el-table-column v-if="newScheduleRequest.serviceLine === 'Professional Fee'" prop="specialties" label="Specialties">
                  <template slot-scope="scope">
                    <ul class="table-list">
                      <li v-for="specialty in scope.row.specialties.filter(x => x.selected)" :key="specialty.key">
                        {{specialty.key}} - {{getCodingDetailsText(specialty)}}</li>
                    </ul>
                  </template>
                </el-table-column>
                <el-table-column prop="scheduledDays" label="Scheduled Days" width="120">
                  <template slot-scope="scope">
                    <span>{{scope.row.scheduledDays.length}}
                      {{ scope.row.scheduledDays.length === 1 ? 'Day' : 'Days'  }}</span>
                  </template>
                </el-table-column>
              </el-table>

              <el-form size="mini" style="margin: 20px 10px 0px 10px;">
                <el-form-item label="Additional Notes">
                  <el-input type="textarea" rows="3" v-model="newScheduleRequest.notes"></el-input>
                </el-form-item>
              </el-form>

              <el-button plain style="margin-top: 30px; margin-bottom: 20px" @click="modifyPrevious">
                Modify Previous <i style="padding-left: 5px;" class="el-icon-arrow-up"></i>
              </el-button>

              <el-button type="primary" style="margin-top: 30px;" @click="completeStepTwo">
                Submit Request
              </el-button>
              <br />
              <hia-loading style="margin-top: 40px;" v-if="submitting"></hia-loading>
            </div>
            <!-- Step 3 -->
            <div key="step3" v-if="activeStep === 3">
              <h4>Thank you, we have received your request. We will review and confirm the availability of our staff. A
                final
                schedule will be sent prior to the start date.</h4>
              <router-link :to="`/${currentClientId}/requests`">
                <el-button type="primary">Return to Request List</el-button>
              </router-link>
            </div>
          </transition>

        </el-col>
        <el-col :span="4">
          <ul class="actionButtons" style="margin-top: 0">
            <li>
              <!-- <el-button type="primary">New Request</el-button> -->
            </li>
          </ul>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import CodingRequestAcuteCoderDetails from './CodingRequestAcuteCoderDetails'
  import CodingRequestProCoderDetails from './CodingRequestProCoderDetails'
  import MasterCalendarPicker from '@/components/CalendarPicker/MasterCalendarPicker'

  export default {
    data: function () {
      return {
        activeStep: 0,
        getCodingDetailsText: CodingRequestProCoderDetails.methods.getCodingDetailsText,
        loading: true,
        submitting: false,
        newScheduleRequest: {
          month: '',
          serviceLine: 'Acute',
          notes: '',
          coders: [],
          rules: {
            month: [
              { required: true, message: 'Please Select Requested Month', trigger: 'change' }
            ]
          }
        }
      }
    },
    name: 'NewCodingRequest',
    methods: {
      ...mapActions('requests/', ['GET_REQUESTS', 'CHECK_EXISTS', 'POST_CODING_SCHEDULE_REQUEST', 'GET_SCHEDULE_PERIODS']),
      initialize() {
        this.loading = true
        this.GET_SCHEDULE_PERIODS().then(_ => {
          this.loading = false
        })
      },
      completeStepZero() {
        this.$refs['newScheduleRequest'].validate((valid) => {
          if (valid) {
            this.submitting = true
            let selectedMonth = this.getSelectedMonth()
            let payload = {
              requestStartDate: `${selectedMonth.year}-${selectedMonth.month + 1}-1`,
              codingType: this.newScheduleRequest.serviceLine
            }
            this.CHECK_EXISTS(payload).then(() => {
              this.activeStep++
              this.submitting = false
            }, error => {
              let message = error.response.data ? error.response.data.message : error
              this.$message({
                type: 'error',
                message: message,
                duration: 10000
              })
              this.submitting = false
            })
          }
        })
      },
      modifyPrevious() {
        if (this.activeStep === 1 && this.newScheduleRequest.coders.length > 0) {
          this.$confirm(`This will clear all coders from your request, continue?`, 'Modify previous', {
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }).then(() => {
            this.newScheduleRequest.coders = []
            this.activeStep--
          })
        } else {
          this.activeStep--
        }
        window.scrollTo(0, 0)
      },
      completeStepOne() {
        this.activeStep++
        window.scrollTo(0, 0)
      },
      completeStepTwo() {
        this.submitting = true
        let selectedMonth = this.getSelectedMonth()
        let serviceLine = this.newScheduleRequest.serviceLine

        let payload = {
          clientNumber: this.currentClient,
          numberOfCoders: this.newScheduleRequest.coders.length,
          requestStartDate: `${selectedMonth.year}-${selectedMonth.month + 1}-1`,
          codingType: this.newScheduleRequest.serviceLine,
          notes: this.newScheduleRequest.notes,
          scheduleRequestsDetailsCoding: serviceLine === 'Acute' ? this.getFlattenedCodersList(this.newScheduleRequest.coders, serviceLine) : [],
          scheduleRequestsDetailsCodingPro: serviceLine === 'Professional Fee' ? this.getFlattenedCodersList(this.newScheduleRequest.coders, serviceLine) : [],
          scheduleRequestsSpecialtiesCodingPro: serviceLine === 'Professional Fee' ? this.getFlattenedSpecialtiesList(this.newScheduleRequest.coders) : []
        }

        this.POST_CODING_SCHEDULE_REQUEST(payload).then(() => {
          this.activeStep++
          this.submitting = false
        }, error => {
          let message = error.response.data ? error.response.data.message : error
          this.$message({
            type: 'error',
            message: message,
            duration: 10000
          })
          this.submitting = false
        })
      },
      getFlattenedCodersList(coders, serviceLine) {
        let selectedMonth = this.getSelectedMonth()
        let flattenedCodersList = []

        for (let i = 0; i < coders.length; i++) {
          for (let j = 0; j < coders[i].scheduledDays.length; j++) {
            if (serviceLine === 'Professional Fee') {
              flattenedCodersList.push({
                coderNumber: i + 1,
                requestDate: `${selectedMonth.year}-${selectedMonth.month + 1}-${coders[i].scheduledDays[j].date}`
              })
            }
            if (serviceLine === 'Acute') {
              flattenedCodersList.push({
                coderNumber: i + 1,
                onsiteRemote: 'Remote',
                requestDate: `${selectedMonth.year}-${selectedMonth.month + 1}-${coders[i].scheduledDays[j].date}`,
                abstract: false,
                inpatient: coders[i].patientTypes.includes('Inpatient'),
                ambSurg: coders[i].patientTypes.includes('Ambulatory Surgery'),
                anc: coders[i].patientTypes.includes('Ancilliary'),
                ed: coders[i].patientTypes.includes('Emergency Department'),
                oopx: coders[i].patientTypes.includes('Other Outpatients with Procedures')
              })
            }
          }
        }

        return flattenedCodersList
      },
      getFlattenedSpecialtiesList(coders) {
        let flattenedSpecialtiesList = []

        for (let i = 0; i < coders.length; i++) {
          let selectedSpecialties = coders[i].specialties.filter(x => x.selected)
          for (let j = 0; j < selectedSpecialties.length; j++) {
            flattenedSpecialtiesList.push({
              coderNumber: i + 1,
              specialtyId: selectedSpecialties[j].specialtyId,
              isDx: selectedSpecialties[j].isDx,
              isEM: selectedSpecialties[j].isEM,
              isSurgCpt: selectedSpecialties[j].isSurgCpt,
              isHospital: selectedSpecialties[j].isHospital,
              isClinic: selectedSpecialties[j].isClinic
            })
          }
        }
        return flattenedSpecialtiesList
      },
      addCoders(newCoders) {
        this.newScheduleRequest.coders = this.newScheduleRequest.coders.concat(newCoders)
      },
      deleteCoders(coder) {
        this.$confirm(`Delete ${coder.coder} from request?`, 'Delete coder', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          let i = this.newScheduleRequest.coders.findIndex(x => x.coderNumber === coder.coderNumber)
          this.newScheduleRequest.coders.splice(i, 1)
        })
      },
      getNumberOfWorkingDays() {
        var selectedMonth = this.getSelectedMonth()
        var lastDay = new Date(selectedMonth.year, selectedMonth.month + 1, 0).getDate()
        var workingDays = 0
        for (let i = 1; i <= lastDay; i++) {
          let date = new Date(selectedMonth.year, selectedMonth.month, i)
          if (date.getDay() !== 0 && date.getDay() !== 6) {
            workingDays++
          }
        }
        return workingDays
      },
      handlePatientTypeSelect(parent) {
        if (parent.patientTypes.includes('selectAll')) {
          parent.patientTypes = this.patientTypes.map(option => option.key)
        }
      },
      getSelectedMonth() {
        return this.schedulePeriods.find(x => x.key === this.newScheduleRequest.month)
      },
      getCalendarPickerDate() {
        let selectedMonth = this.getSelectedMonth()
        return new Date(selectedMonth.year, selectedMonth.month, 1)
      },
      clearCoders() {
        this.$confirm(`Clear all coders from request?`, 'Clear coders', {
          confirmButtonText: 'Clear',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.newScheduleRequest.coders = []
        })
      }
    },
    created: function () {
      this.initialize()
    },
    computed: {
      ...mapState('user/', ['currentClient', 'currentClientId']),
      ...mapState('requests/', ['schedulePeriods'])
    },
    components: {
      CodingRequestAcuteCoderDetails,
      CodingRequestProCoderDetails,
      MasterCalendarPicker
    }
  }
</script>

<style scoped>
>>> tr .cell {
  font-weight: 500;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ACTION BUTTONS */
.actionButtonsIcon {
  font-size: 18px;
}

/* EL STEPS */
#requestSteps {
  background: rgba(238, 238, 238, 0.5);
  border-radius: 5px;
  padding: 5px 2px 5px 2px;
}

#requestSteps >>> .el-step {
  /* min-height: 150px; */
  flex-basis: auto !important;
}

#requestSteps >>> .el-step__main {
  padding-bottom: 10px;
}

/* step waiting*/
#requestSteps >>> .el-step__description.is-wait {
  padding-right: 0px;
}

#requestSteps >>> .el-step__description.is-wait p {
  margin: 0px 0px 10px 0px !important;
  font-weight: normal !important;
  padding-right: 0px;
}

/* step in process*/
#requestSteps >>> .el-step__description.is-process {
  padding-right: 0px;
}

#requestSteps >>> .el-step__description.is-process p {
  margin: 0px 0px 10px 0px;
  padding-right: 0px;
  font-weight: normal !important;
  color: #606266;
}

#requestSteps >>> .el-step__title.is-process,
#requestSteps >>> .el-step__head.is-process {
  color: #005695;
}

#requestSteps >>> .el-step__head.is-process .el-step__line {
  background-color: #005695;
}

#requestSteps >>> .el-step__head.is-process .el-step__icon.is-text {
  border: 2px solid #005695;
}

/* step success */
#requestSteps >>> .el-step__description.is-success {
  padding-right: 0px;
}

#requestSteps >>> .el-step__description.is-success p {
  margin: 0px 0px 10px 0px;
  padding-right: 0px;
  font-weight: normal !important;
  color: #c0c4cc;
}

#requestSteps >>> .el-step__title.is-success,
#requestSteps >>> .el-step__head.is-success {
  color: #7cbe5f;
}

#requestSteps >>> .el-step__head.is-success .el-step__line {
  background-color: #7cbe5f;
}

#requestSteps >>> .el-step__head.is-success .el-step__icon.is-text {
  border: 2px solid #7cbe5f;
}

/* ADD CALEDNAR */
.addCalendar >>> .el-form-item__content {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 0px 0px 15px 0px;
}

/* request button */
.requestButton {
  font-size: 12px;
  font-weight: 500;
}

.requestButtonIcons {
  font-size: 22px;
  float: right;
  margin: -5px 0px 0px 0px;
  padding: 0px 0px 0px 0px;
  height: 14px;
  width: 14px;
}

>>> .cell {
  word-break: inherit !important;
}

>>> .el-table__empty-block {
  min-height: 39px;
}

>>> .table-list {
  display: table-cell;
  vertical-align: middle;
  list-style-type: none;
  padding: 0px;
}
</style>
